import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TabsModule } from 'primeng/tabs';
import { TranslocoPipe } from '@jsverse/transloco';
import { CrmClient } from '../crm/crm.model';
import { CrmService } from '../crm/crm.service';
import { HrEmployee } from '../hr/employee/hr-employee.models';
import { HrService } from '../hr/hr.service';
import { Party } from '../party/party.model';
import { PartyService } from '../party/party.service';
import { SessionStorageService } from '../services/session-storage.service';
import { app_permissions } from '../security/app-permissions';
import { PermissionService } from '../security/permission.service';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageMetric } from '../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import {
  EquipmentCategory, EquipmentRateCard, EquipmentResource, EquipmentUsage,
  FieldCapability, FieldFinanceLink, FieldLaborRoleOption, FieldLaborWorkEntry,
  FieldProficiencyLevel, FieldSettlementSummary,
  FieldWorker, FieldWorkerCapability, FieldWorkPackage, FieldWorkSummary
} from './field-work.models';
import { FieldWorkService } from './field-work.service';
import { EquipmentRateCreateDialogComponent } from './equipment-rate-create-dialog.component';
import { EquipmentResourceCreateDialogComponent } from './equipment-resource-create-dialog.component';
import { FieldPersonCreateDialogComponent } from './field-person-create-dialog.component';
import { FieldWorkerCapabilityDialogComponent } from './field-worker-capability-dialog.component';
import { FieldWorkSetupCreateDialogComponent, FieldWorkSetupDialogType, FieldWorkSetupRecord } from './field-work-setup-create-dialog.component';
import { PartyQuickCreateDialogComponent, PartyQuickCreateResult } from '../shared/party-quick-create/party-quick-create-dialog.component';
import { Laborer } from './laborer.model';

export type FieldWorkPage = 'packages' | 'schedule' | 'assignments' | 'approvals'
  | 'people' | 'setup' | 'equipment' | 'rates' | 'exceptions';

@Component({
  selector: 'app-field-work-workbench',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ButtonModule, DatePickerModule, InputNumberModule, InputTextModule,
    ProgressBarModule, SelectModule, TableModule, TagModule, TabsModule, TranslocoPipe,
    PageShellComponent, PageHeroComponent, PageMetricStripComponent, PageDataPanelComponent,
    EquipmentRateCreateDialogComponent, EquipmentResourceCreateDialogComponent, FieldPersonCreateDialogComponent,
    FieldWorkerCapabilityDialogComponent, FieldWorkSetupCreateDialogComponent, PartyQuickCreateDialogComponent
  ],
  templateUrl: './field-work-workbench.component.html',
  styleUrl: './field-work-workbench.component.scss'
})
export class FieldWorkWorkbenchComponent implements OnInit, OnDestroy {
  private readonly service = inject(FieldWorkService);
  private readonly permissions = inject(PermissionService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly hrService = inject(HrService);
  private readonly crmService = inject(CrmService);
  private readonly partyService = inject(PartyService);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly subscriptions = new Subscription();

  page: FieldWorkPage = 'packages';
  loading = false;
  error = '';
  summary: FieldWorkSummary = {};
  packages: FieldWorkPackage[] = [];
  categories: EquipmentCategory[] = [];
  resources: EquipmentResource[] = [];
  rates: EquipmentRateCard[] = [];
  exceptions: FieldFinanceLink[] = [];
  settlements: FieldSettlementSummary[] = [];
  pendingLabor: FieldLaborWorkEntry[] = [];
  pendingEquipment: EquipmentUsage[] = [];
  people: FieldWorker[] = [];
  capabilityPeople: FieldWorker[] = [];
  peopleTotalRecords = 0;
  peopleRows = 20;
  peopleFirst = 0;
  peopleTableLoading = false;
  roles: FieldLaborRoleOption[] = [];
  capabilities: FieldCapability[] = [];
  proficiencyLevels: FieldProficiencyLevel[] = [];
  locations: Array<{ id?: number; name?: string; shortDescription?: string }> = [];
  employees: HrEmployee[] = [];
  supplierClients: CrmClient[] = [];
  rateClients: CrmClient[] = [];
  parties: Party[] = [];
  newlyCreatedLaborer: Party | null = null;
  approvalReasons: Record<string, string> = {};
  bulkApprovalLoading = false;
  personDialogVisible = false;
  laborerQuickCreateVisible = false;
  workerCapabilityDialogVisible = false;
  resourceDialogVisible = false;
  rateDialogVisible = false;
  savingPerson = false;
  savingWorkerCapability = false;
  savingResource = false;
  savingRate = false;
  setupDialogVisible = false;
  setupDialogType: FieldWorkSetupDialogType = 'role';
  savingSetup = false;
  activeSetupTab: FieldWorkSetupDialogType = 'role';

  get canViewFieldWork(): boolean {
    return this.permissions.hasPermission(app_permissions.operations.fieldWork.view);
  }
  get canViewEquipment(): boolean {
    return this.permissions.hasPermission(app_permissions.operations.equipment.view);
  }
  get canManageFieldWork(): boolean {
    return this.permissions.hasPermission(app_permissions.operations.fieldWork.manage);
  }
  get canManageEquipment(): boolean {
    return this.permissions.hasPermission(app_permissions.operations.equipment.manage);
  }
  get canViewRates(): boolean {
    return this.permissions.hasAnyPermission([
      app_permissions.operations.equipment.ratesView,
      app_permissions.operations.equipment.ratesManage,
      app_permissions.operations.fieldWork.financeView
    ]);
  }
  get canManageRates(): boolean {
    return this.permissions.hasPermission(app_permissions.operations.equipment.ratesManage);
  }
  get canViewFinance(): boolean {
    return this.permissions.hasPermission(app_permissions.operations.fieldWork.financeView);
  }
  get canApprove(): boolean {
    return this.permissions.hasPermission(app_permissions.operations.fieldWork.approve);
  }
  get canGenerateFinance(): boolean {
    return this.permissions.hasPermission(app_permissions.operations.fieldWork.financeGenerate);
  }

  get pageTitle(): string {
    return {
      packages: 'Work Packages', schedule: 'Field Work Schedule', assignments: 'Resource Assignments',
      approvals: 'Field Work Approvals', people: 'Field People', setup: 'Field Work Setup',
      equipment: 'Tools & Equipment', rates: 'Equipment Rate Cards',
      exceptions: 'Finance Exceptions'
    }[this.page];
  }

  get locationOptions(): Array<{ label: string; value: number }> {
    return this.locations.filter(location => Boolean(location.id)).map(location => ({
      label: [location.name, location.shortDescription].filter(Boolean).join(' — '),
      value: location.id!
    }));
  }

  get pageSubtitle(): string {
    return {
      packages: 'Plan and open field activities attached to operational records.',
      schedule: 'Review planned field activity, location, timing, and coverage.',
      assignments: 'Monitor labor and equipment coverage across active work packages.',
      approvals: 'Review submitted labor actuals and equipment usage before finance handoff.',
      people: 'Maintain operational workers and the capabilities used for field assignment eligibility.',
      setup: 'Configure Field Work roles, capabilities, proficiency levels, and reusable equipment categories independently of HR.',
      equipment: 'Maintain reusable operational tools and equipment outside Fleet and Warehousing.',
      rates: 'Maintain effective-dated buy and sell rates for reusable equipment.',
      exceptions: 'Resolve finance handoff failures without losing source traceability.'
    }[this.page];
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Open work packages', value: this.summary.openPackages || 0, tone: 'info' },
      { label: 'Pending approvals', value: this.summary.pendingApprovals || 0,
        tone: (this.summary.pendingApprovals || 0) > 0 ? 'warn' : 'default' },
      { label: 'Active equipment', value: this.summary.activeEquipment || 0, tone: 'success' },
      { label: 'Equipment in use', value: this.summary.equipmentInUse || 0, tone: 'info' },
      { label: 'Finance exceptions', value: this.summary.financeExceptions || 0,
        tone: (this.summary.financeExceptions || 0) > 0 ? 'danger' : 'default' }
    ];
  }

  ngOnInit(): void {
    this.subscriptions.add(this.route.data.subscribe(data => {
      this.page = (data['fieldWorkPage'] as FieldWorkPage) || 'packages';
      this.refresh();
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  refresh(): void {
    this.loading = true;
    this.error = '';
    if (this.canViewFieldWork) {
      this.subscriptions.add(this.service.summary().subscribe({
        next: value => this.summary = value,
        error: error => this.capture(error)
      }));
    }

    if (['packages', 'schedule', 'assignments'].includes(this.page)) {
      this.subscriptions.add(this.service.packages().subscribe({
        next: value => { this.packages = value; this.loading = false; },
        error: error => this.finishWithError(error)
      }));
      return;
    }
    if (this.page === 'approvals') {
      let remaining = 2;
      const finished = () => { remaining -= 1; if (remaining === 0) this.loading = false; };
      this.subscriptions.add(this.service.laborWorkEntries().subscribe({
        next: value => { this.pendingLabor = value.filter(row => row.entryStatus === 'SUBMITTED'); finished(); },
        error: error => { this.capture(error); finished(); }
      }));
      this.subscriptions.add(this.service.equipmentUsages('SUBMITTED').subscribe({
        next: value => { this.pendingEquipment = value; finished(); },
        error: error => { this.capture(error); finished(); }
      }));
      return;
    }
    if (this.page === 'people') {
      let remaining = 5;
      const finished = () => { remaining -= 1; if (remaining === 0) this.loading = false; };
      this.subscriptions.add(this.service.capabilities().subscribe({
        next: value => { this.capabilities = value; finished(); },
        error: error => { this.capture(error); finished(); }
      }));
      this.subscriptions.add(this.service.proficiencyLevels().subscribe({
        next: value => { this.proficiencyLevels = value; finished(); },
        error: error => { this.capture(error); finished(); }
      }));
      this.loadPersonDialogOptions(finished);
      return;
    }
    if (this.page === 'setup') {
      let remaining = 4;
      const finished = () => { remaining -= 1; if (remaining === 0) this.loading = false; };
      this.subscriptions.add(this.service.laborRoles().subscribe({
        next: value => { this.roles = value; finished(); },
        error: error => { this.capture(error); finished(); }
      }));
      this.subscriptions.add(this.service.capabilities().subscribe({
        next: value => { this.capabilities = value; finished(); },
        error: error => { this.capture(error); finished(); }
      }));
      this.subscriptions.add(this.service.proficiencyLevels().subscribe({
        next: value => { this.proficiencyLevels = value; finished(); },
        error: error => { this.capture(error); finished(); }
      }));
      this.subscriptions.add(this.service.categories().subscribe({
        next: value => { this.categories = value; finished(); },
        error: error => { this.capture(error); finished(); }
      }));
      return;
    }
    if (this.page === 'equipment') {
      let remaining = 4;
      const finished = () => { remaining -= 1; if (remaining === 0) this.loading = false; };
      this.subscriptions.add(this.service.categories().subscribe({
        next: value => { this.categories = value; finished(); },
        error: error => { this.capture(error); finished(); }
      }));
      this.subscriptions.add(this.service.resources().subscribe({
        next: value => { this.resources = value; finished(); },
        error: error => { this.capture(error); finished(); }
      }));
      this.loadSupplierAndPartyOptions(finished);
      return;
    }
    if (this.page === 'rates' && this.canViewRates) {
      let remaining = (this.canViewEquipment ? 3 : 1) + (this.canManageRates ? 1 : 0);
      const finished = () => { remaining -= 1; if (remaining === 0) this.loading = false; };
      if (this.canViewEquipment) {
        this.subscriptions.add(this.service.categories().subscribe({
          next: value => { this.categories = value; finished(); },
          error: error => { this.capture(error); finished(); }
        }));
        this.subscriptions.add(this.service.resources().subscribe({
          next: value => { this.resources = value; finished(); },
          error: error => { this.capture(error); finished(); }
        }));
      }
      this.subscriptions.add(this.service.rates().subscribe({
        next: value => { this.rates = value; finished(); },
        error: error => { this.capture(error); finished(); }
      }));
      if (this.canManageRates) {
        this.subscriptions.add(this.crmService.getClients(undefined, ['CUSTOMER']).subscribe({
          next: value => { this.rateClients = value || []; finished(); },
          error: () => { this.rateClients = []; finished(); }
        }));
      }
      return;
    }
    if (this.page === 'exceptions' && this.canViewFinance) {
      let remaining = 2;
      const finished = () => { remaining -= 1; if (remaining === 0) this.loading = false; };
      this.subscriptions.add(this.service.financeExceptions().subscribe({
        next: value => { this.exceptions = value; finished(); },
        error: error => { this.capture(error); finished(); }
      }));
      this.subscriptions.add(this.service.settlementSummaries().subscribe({
        next: value => { this.settlements = value; finished(); },
        error: error => { this.capture(error); finished(); }
      }));
      return;
    }
    this.loading = false;
  }

  openPackage(row: FieldWorkPackage): void {
    if (row.fieldWorkPackageId) {
      this.router.navigate(['/operations/field-work/work-packages', row.fieldWorkPackageId]);
    }
  }

  openSetupDialog(type: FieldWorkSetupDialogType): void {
    this.error = '';
    this.setupDialogType = type;
    this.setupDialogVisible = true;
  }

  roleCapabilityLabels(role: FieldLaborRoleOption): string {
    return (role.mandatoryCapabilities ?? [])
      .map(capability => capability.skillName || `Capability #${capability.fieldCapabilityId}`)
      .join(', ') || 'None';
  }

  onSetupTabChange(value: string | number): void {
    if (value === 'role' || value === 'capability' || value === 'proficiency' || value === 'equipment-category') {
      this.activeSetupTab = value;
    }
  }

  saveSetup(value: FieldWorkSetupRecord): void {
    this.savingSetup = true;
    switch (this.setupDialogType) {
      case 'role':
        this.completeSetupSave(this.service.saveFieldRole(value as FieldLaborRoleOption));
        return;
      case 'capability':
        this.completeSetupSave(this.service.saveCapability(value as FieldCapability));
        return;
      case 'proficiency':
        this.completeSetupSave(this.service.saveProficiencyLevel(value as FieldProficiencyLevel));
        return;
      default:
        this.completeSetupSave(this.service.saveCategory(value as EquipmentCategory));
    }
  }

  private completeSetupSave(save: Observable<unknown>): void {
    this.subscriptions.add(save.subscribe({
      next: () => {
        this.savingSetup = false;
        this.setupDialogVisible = false;
        this.refresh();
      },
      error: error => { this.savingSetup = false; this.capture(error); }
    }));
  }

  openResourceDialog(): void {
    this.error = '';
    this.resourceDialogVisible = true;
  }

  saveResource(resource: EquipmentResource): void {
    if (resource.ownershipType === 'RENTAL' && resource.financialTreatment === 'INTERNAL_COST') {
      resource.financialTreatment = 'RENTAL_PAYABLE';
    }
    this.savingResource = true;
    this.service.saveResource(resource).subscribe({
      next: () => { this.savingResource = false; this.resourceDialogVisible = false; this.refresh(); },
      error: error => { this.savingResource = false; this.capture(error); }
    });
  }

  openRateDialog(): void {
    this.error = '';
    this.rateDialogVisible = true;
  }

  saveRate(rate: EquipmentRateCard): void {
    this.savingRate = true;
    this.service.saveRate(rate).subscribe({
      next: () => { this.savingRate = false; this.rateDialogVisible = false; this.refresh(); },
      error: error => { this.savingRate = false; this.capture(error); }
    });
  }

  openPersonDialog(): void {
    this.error = '';
    this.newlyCreatedLaborer = null;
    this.personDialogVisible = true;
  }

  openLaborerQuickCreate(): void {
    this.laborerQuickCreateVisible = true;
  }

  laborerCreated(result: PartyQuickCreateResult): void {
    const laborer = result.record as Laborer;
    this.laborerQuickCreateVisible = false;
    this.loadSupplierAndPartyOptions(() => undefined);
    if (laborer?.id) {
      this.newlyCreatedLaborer = { id: { entityId: laborer.id, entityType: 'Laborer' }, entityName: laborer.name,
        emailAddress: laborer.emailAddress, phoneNumber: laborer.phoneNumber, imageUrl: laborer.imageUrl } as Party;
      this.parties = [this.newlyCreatedLaborer, ...this.parties.filter(row => row.id?.entityId !== laborer.id
        || row.id?.entityType !== 'Laborer')];
    }
  }

  openWorkerCapabilityDialog(): void {
    this.error = '';
    this.workerCapabilityDialogVisible = true;
    this.searchCapabilityPeople('');
  }

  loadPeople(event: TableLazyLoadEvent = { first: this.peopleFirst, rows: this.peopleRows }): void {
    const rows = Math.max(1, event.rows || this.peopleRows);
    const first = Math.max(0, event.first || 0);
    const page = Math.floor(first / rows);
    this.peopleRows = rows;
    this.peopleFirst = first;
    this.peopleTableLoading = true;
    this.subscriptions.add(this.service.people({ page, pageSize: rows }).subscribe({
      next: value => {
        this.people = value.content || [];
        this.peopleTotalRecords = value.totalElements || 0;
        this.peopleFirst = (value.number ?? page) * rows;
        this.peopleTableLoading = false;
      },
      error: error => {
        this.people = [];
        this.peopleTotalRecords = 0;
        this.peopleTableLoading = false;
        this.capture(error);
      }
    }));
  }

  searchCapabilityPeople(search: string): void {
    this.subscriptions.add(this.service.people({ page: 0, pageSize: 50, search }).subscribe({
      next: value => this.capabilityPeople = value.content || [],
      error: error => this.capture(error)
    }));
  }

  savePerson(person: FieldWorker): void {
    this.savingPerson = true;
    this.service.savePerson(person).subscribe({
      next: () => { this.savingPerson = false; this.personDialogVisible = false; this.refresh(); },
      error: error => { this.savingPerson = false; this.capture(error); }
    });
  }

  saveWorkerCapability(value: FieldWorkerCapability & { fieldWorkerId?: number }): void {
    const workerId = value.fieldWorkerId;
    if (!workerId) return;
    this.savingWorkerCapability = true;
    this.service.saveWorkerCapability(workerId, value).subscribe({
      next: () => {
        this.savingWorkerCapability = false;
        this.workerCapabilityDialogVisible = false;
        this.loadPeople();
      },
      error: error => { this.savingWorkerCapability = false; this.capture(error); }
    });
  }

  retryException(row: FieldFinanceLink): void {
    if (!row.fieldFinanceLinkId) return;
    this.service.retryFinanceException(row.fieldFinanceLinkId, row.sourceKind).subscribe({
      next: () => this.refresh(), error: error => this.capture(error)
    });
  }

  approveLabor(row: FieldLaborWorkEntry): void {
    if (!row.fieldLaborWorkEntryId) return;
    this.service.laborWorkEntryAction(row.fieldLaborWorkEntryId, 'approve').subscribe({
      next: () => this.refresh(), error: error => this.capture(error)
    });
  }

  approveEquipment(row: EquipmentUsage): void {
    if (!row.fieldEquipmentUsageId) return;
    this.service.usageAction(row.fieldEquipmentUsageId, 'approve').subscribe({
      next: () => this.refresh(), error: error => this.capture(error)
    });
  }

  rejectLabor(row: FieldLaborWorkEntry): void {
    if (!row.fieldLaborWorkEntryId) return;
    const reason = this.approvalReasons[`labor-${row.fieldLaborWorkEntryId}`]?.trim();
    if (!reason) {
      this.error = 'Enter a rejection reason for the labor actual.';
      return;
    }
    this.service.laborWorkEntryAction(row.fieldLaborWorkEntryId, 'reject', { reason }).subscribe({
      next: () => this.refresh(), error: error => this.capture(error)
    });
  }

  rejectEquipment(row: EquipmentUsage): void {
    if (!row.fieldEquipmentUsageId) return;
    const reason = this.approvalReasons[`equipment-${row.fieldEquipmentUsageId}`]?.trim();
    if (!reason) {
      this.error = 'Enter a rejection reason for the equipment usage.';
      return;
    }
    this.service.usageAction(row.fieldEquipmentUsageId, 'reject', { reason }).subscribe({
      next: () => this.refresh(), error: error => this.capture(error)
    });
  }

  approveAllSubmitted(): void {
    const laborWorkEntryIds = this.pendingLabor
      .map(row => row.fieldLaborWorkEntryId).filter((id): id is number => Boolean(id));
    const equipmentUsageIds = this.pendingEquipment
      .map(row => row.fieldEquipmentUsageId).filter((id): id is number => Boolean(id));
    if (!laborWorkEntryIds.length && !equipmentUsageIds.length) return;
    this.bulkApprovalLoading = true;
    this.service.bulkApproval({ laborWorkEntryIds, equipmentUsageIds, action: 'APPROVE' }).subscribe({
      next: result => {
        this.bulkApprovalLoading = false;
        if (result.failed) this.error = `${result.failed} approval(s) failed: ${(result.errors || []).join('; ')}`;
        this.refresh();
      },
      error: error => { this.bulkApprovalLoading = false; this.capture(error); }
    });
  }

  packageStatus(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (['RELEASED', 'IN_PROGRESS', 'COMPLETED'].includes(status || '')) return 'success';
    if (['DRAFT', 'PLANNED'].includes(status || '')) return 'warn';
    if (status === 'CANCELLED') return 'danger';
    return 'info';
  }

  ownershipSeverity(ownership?: string): 'success' | 'info' | 'warn' {
    return ownership === 'RENTAL' ? 'warn' : ownership === 'OWNED' ? 'success' : 'info';
  }

  workerSourceSeverity(source?: string): 'success' | 'info' | 'warn' {
    return source === 'SUPPLIER_WORKER' ? 'warn' : source === 'INTERNAL' ? 'success' : 'info';
  }

  assignedLabor(row: FieldWorkPackage): number {
    return row.laborAssignments?.length || 0;
  }

  requiredLabor(row: FieldWorkPackage): number {
    return (row.laborRequirements || []).reduce((total, item) => total + (item.headcount || 0), 0);
  }

  allocatedEquipment(row: FieldWorkPackage): number {
    return (row.equipmentAllocations || []).reduce((total, item) => total + (item.plannedQuantity || 0), 0);
  }

  requiredEquipment(row: FieldWorkPackage): number {
    return (row.equipmentRequirements || []).reduce((total, item) => total + (item.requiredQuantity || 0), 0);
  }

  private loadPersonDialogOptions(finished: () => void): void {
    this.subscriptions.add(this.hrService.employees({ page: 0, pageSize: 500 }).subscribe({
      next: value => { this.employees = value.items || []; finished(); },
      error: () => { this.employees = []; finished(); }
    }));
    this.loadSupplierAndPartyOptions(finished);
  }

  private loadSupplierAndPartyOptions(finished: () => void): void {
    this.subscriptions.add(this.crmService.getClients(undefined,
      ['SUPPLIER', 'WAREHOUSE_SUPPLIER', 'TRANSPORTER']).subscribe({
      next: value => { this.supplierClients = value || []; finished(); },
      error: error => { this.capture(error); finished(); }
    }));
    const companyId = this.sessionStorage.getCompany()?.id || 0;
    this.subscriptions.add(this.partyService.getParties(companyId,
      ['Employee', 'Laborer', 'Company', 'TransportationAgency'], '', 0, 1000).subscribe({
      next: value => { this.parties = value.content || []; finished(); },
      error: error => { this.capture(error); finished(); }
    }));
  }

  private finishWithError(error: unknown): void {
    this.loading = false;
    this.capture(error);
  }

  private capture(error: any): void {
    this.error = error?.error?.message || error?.error?.error || error?.message
      || 'Field work data could not be loaded.';
  }
}
